// src/pages/cadastros/FaixasExcessoPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Form, InputGroup, Row, Col,
  Modal, Spinner, Alert, Pagination
} from 'react-bootstrap';
import { FaSearch, FaPlus, FaEdit, FaTrash, FaExclamationTriangle, FaSave } from 'react-icons/fa';
import useSortable from '../../hooks/useSortable';
import SortableTh from '../../components/SortableTh';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import api from '../../service/api';

interface FaixaExcesso {
  CODIGO: number;
  FAIXA: string;
  INICIO: number;
  FIM: number;
  CUSTO_M3: number;
  VALOR_INICIAL: number;
}

interface FaixaFormValues {
  FAIXA: string;
  INICIO: number;
  FIM: number;
  CUSTO_M3: number;
  VALOR_INICIAL: number;
}

const ITEMS_PER_PAGE = 10;

const validationSchema = Yup.object({
  FAIXA: Yup.string().required('Descrição da faixa é obrigatória').max(50),
  INICIO: Yup.number().required().min(0),
  FIM: Yup.number().required().min(0).test('maior-inicio', 'Deve ser maior que o início', function (v) {
    return v !== undefined ? v > this.parent.INICIO : true;
  }),
  CUSTO_M3: Yup.number().required('Custo/m³ é obrigatório').min(0),
  VALOR_INICIAL: Yup.number().min(0),
});

const emptyForm: FaixaFormValues = { FAIXA: '', INICIO: 0, FIM: 0, CUSTO_M3: 0, VALOR_INICIAL: 0 };

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const FaixasExcessoPage: React.FC = () => {
  const [faixas, setFaixas] = useState<FaixaExcesso[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [currentFaixa, setCurrentFaixa] = useState<FaixaExcesso | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [faixaToDelete, setFaixaToDelete] = useState<FaixaExcesso | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchFaixas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/faixas-excesso');
      setFaixas(res.data);
      setCurrentPage(1);
    } catch {
      setError('Não foi possível carregar as faixas de excesso.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFaixas(); }, [fetchFaixas]);

  const filtered = faixas.filter(f =>
    f.FAIXA?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const { sortedData: sortedFaixas, sortKey, sortDir, handleSort } = useSortable<FaixaExcesso>(filtered, 'INICIO');
  const paginated = sortedFaixas.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const handleSubmit = async (
    values: FaixaFormValues,
    { resetForm }: FormikHelpers<FaixaFormValues>
  ) => {
    try {
      setFormSubmitting(true);
      if (modalMode === 'create') {
        await api.post('/faixas-excesso', values);
      } else {
        await api.put(`/faixas-excesso/${currentFaixa!.CODIGO}`, values);
      }
      setShowModal(false);
      resetForm();
      fetchFaixas();
    } catch {
      setError('Não foi possível salvar. Tente novamente.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!faixaToDelete) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/faixas-excesso/${faixaToDelete.CODIGO}`);
      setShowDeleteModal(false);
      setFaixaToDelete(null);
      fetchFaixas();
    } catch {
      setError('Não foi possível excluir. Tente novamente.');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Faixas de Excesso</h1>
        <Button variant="primary" onClick={() => { setModalMode('create'); setCurrentFaixa(null); setShowModal(true); }}>
          {FaPlus({ className: "me-2" })} Nova Faixa
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{FaExclamationTriangle({ className: "me-2" })}{error}</Alert>}

      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>{FaSearch({})}</InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por faixa..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </InputGroup>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end align-items-center">
              <span className="text-muted">Total: {filtered.length}</span>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-3">Carregando...</p></div>
          ) : filtered.length === 0 ? (
            <Alert variant="info">Nenhuma faixa encontrada.</Alert>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover>
                  <thead className="table-light">
                    <tr>
                      <SortableTh<FaixaExcesso> label="Código" sortKey="CODIGO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<FaixaExcesso> label="Faixa" sortKey="FAIXA" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<FaixaExcesso> label="Início (m³)" sortKey="INICIO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<FaixaExcesso> label="Fim (m³)" sortKey="FIM" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<FaixaExcesso> label="Custo/m³" sortKey="CUSTO_M3" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<FaixaExcesso> label="Valor Inicial" sortKey="VALOR_INICIAL" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map(f => (
                      <tr key={f.CODIGO}>
                        <td>{f.CODIGO}</td>
                        <td>{f.FAIXA}</td>
                        <td>{f.INICIO}</td>
                        <td>{f.FIM}</td>
                        <td>{formatCurrency(f.CUSTO_M3)}</td>
                        <td>{formatCurrency(f.VALOR_INICIAL)}</td>
                        <td>
                          <Button variant="outline-primary" size="sm" className="me-2" onClick={() => { setModalMode('edit'); setCurrentFaixa(f); setShowModal(true); }} title="Editar">{FaEdit({})}</Button>
                          <Button variant="outline-danger" size="sm" onClick={() => { setFaixaToDelete(f); setShowDeleteModal(true); }} title="Excluir">{FaTrash({})}</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                      .map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {arr[idx - 1] && p - arr[idx - 1] > 1 && <Pagination.Ellipsis disabled />}
                          <Pagination.Item active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
                        </React.Fragment>
                      ))}
                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal Criar/Editar */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'create' ? 'Nova Faixa de Excesso' : 'Editar Faixa'}</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={currentFaixa
            ? { FAIXA: currentFaixa.FAIXA, INICIO: currentFaixa.INICIO, FIM: currentFaixa.FIM, CUSTO_M3: currentFaixa.CUSTO_M3, VALOR_INICIAL: currentFaixa.VALOR_INICIAL }
            : emptyForm}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit: fs }) => (
            <Form onSubmit={fs}>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Descrição da Faixa *</Form.Label>
                  <Form.Control name="FAIXA" value={values.FAIXA} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.FAIXA && !!errors.FAIXA} placeholder="Ex: Faixa 1 - Básico" />
                  <Form.Control.Feedback type="invalid">{errors.FAIXA}</Form.Control.Feedback>
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Início (m³) *</Form.Label>
                      <Form.Control type="number" step="0.01" name="INICIO" value={values.INICIO} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.INICIO && !!errors.INICIO} />
                      <Form.Control.Feedback type="invalid">{errors.INICIO}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fim (m³) *</Form.Label>
                      <Form.Control type="number" step="0.01" name="FIM" value={values.FIM} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.FIM && !!errors.FIM} />
                      <Form.Control.Feedback type="invalid">{errors.FIM}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Custo por m³ (R$) *</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>R$</InputGroup.Text>
                        <Form.Control type="number" step="0.01" name="CUSTO_M3" value={values.CUSTO_M3} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.CUSTO_M3 && !!errors.CUSTO_M3} />
                        <Form.Control.Feedback type="invalid">{errors.CUSTO_M3}</Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Valor Inicial (R$)</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>R$</InputGroup.Text>
                        <Form.Control type="number" step="0.01" name="VALOR_INICIAL" value={values.VALOR_INICIAL} onChange={handleChange} onBlur={handleBlur} />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button variant="primary" type="submit" disabled={formSubmitting}>
                  {formSubmitting ? 'Salvando...' : <>{FaSave({ className: "me-2" })}Salvar</>}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Modal Excluir */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirmar Exclusão</Modal.Title></Modal.Header>
        <Modal.Body className="text-center py-4">
          {FaExclamationTriangle({ size: 48, className: "text-danger mb-3" })}
          <p>Excluir a faixa <strong>{faixaToDelete?.FAIXA}</strong>?</p>
          <p className="text-danger small">Esta ação não pode ser desfeita.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={deleteLoading}>{deleteLoading ? 'Excluindo...' : 'Excluir'}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FaixasExcessoPage;
